import { AfterPromise } from "@/structures";
import { errorDialog } from "../errorDialog";

export class PjElement extends HTMLElement {
  private _ready = new AfterPromise();
  private _disconnected = new AbortController();
  private _hasInitializedOnce = false;

  /**
   * A promise that resolves when the connected element has finished rendering.
   *
   * When disconnected, the `Promise` is reset to a new promise that will resolve if
   * and when the component is connected again.
   */
  get ready(): Promise<void> {
    return this._ready.resolved;
  }

  /**
   * An `AbortSignal` that is aborted when the component is disconnected.
   *
   * When disconnected, the current signal is aborted and a new signal is created that
   * will be aborted during the following disconnect. This means that the `AbortSignal`
   * returned from this property will never be `aborted` at the time that the property
   * is accessed.
   * */
  protected get disconnected(): AbortSignal {
    return this._disconnected.signal;
  }

  /**
   * Lifecycle hook called by the browser when the element is placed within the DOM.
   * Calls `initialize`; implementers should generally override that member instead.
   */
  connectedCallback(): void {
    this._initialize();
  }

  /**
   * Lifecycle hook called by the browser when the element is removed from the DOM.
   * Where possible, implementers should correspond disconnect logic with the
   * `PjElement.disconnected` property instead of overriding this member.
   *
   * The base implementation signals the `disconnected` `AbortSignal` and rejects
   * the `ready` promise if it is still pending, and then resets both.
   *  */
  disconnectedCallback(): void {
    const error = new DOMException(
      "The component has been removed from the document.",
      "ComponentDisconnected",
    );

    this._disconnected.abort(error);
    this._disconnected = new AbortController();

    // In case `ready` was not raised before the component was disconnected
    this._ready.reject(error);
    this._ready = new AfterPromise();
  }

  /**
   * Lifecycle hook called by the browser when this element is moved from one
   * location in the DOM to another (on some browsers) instead of calling
   * `disconnectedCallback` and `connectedCallback`.
   */
  connectedMoveCallback(): void {}

  /** Called when an attribute is changed. Ensure the component implements
   * `static get observedAttributes(): readonly string[]` if this function
   * is implemented, because this function will only be called for those attributes.
   */
  protected attributeChangedCallback?(
    name: string,
    oldValue: string | null,
    newValue: string | null,
  ): void {}

  /**
   * Called when the component is connected to the DOM.
   *
   * @param first Whether this is the first time the component is rendering.
   */
  protected initialize(first: boolean): void | Promise<void> {}

  /**
   * Calls `initialize` and then resolves `ready` when it has finished.
   * Opens `errorDialog` if `initialize` throws.
   */
  private async _initialize(): Promise<void> {
    try {
      const first = !this._hasInitializedOnce;
      this._hasInitializedOnce = true;
      await this.initialize(first);
    } catch (e) {
      errorDialog(e);
    } finally {
      this._ready.resolve();
    }
  }
}
